import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownToLine, Send, Shield, CheckCircle, AlertCircle, Loader2, Settings, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3944';
const UNIFIED_API = import.meta.env.VITE_UNIFIED_API_URL || 'https://cd.merchants.gofreshpay.com/api/backoffice';

interface WalletBalance {
  currency: string;
  balance: number;
  mode: string;
}

type Step = 'form' | 'otp' | 'result';

const OPERATORS = [
  { value: 'airtel', label: 'Airtel', logo: '/airtel.png' },
  { value: 'mpesa', label: 'M-Pesa', logo: '/mpesa.png' },
  { value: 'orange', label: 'Orange', logo: '/orange.png' },
  { value: 'afrimoney', label: 'Afrimoney', logo: '/afrimoney.png' },
];

export default function CardWithdraw() {
  const [step, setStep] = useState<Step>('form');
  const [balances, setBalances] = useState<WalletBalance[]>([]);
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Form
  const [method, setMethod] = useState('');
  const [currency, setCurrency] = useState('');
  const [amount, setAmount] = useState('');

  // OTP
  const [reference, setReference] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpMessage, setOtpMessage] = useState('');

  // Result
  const [result, setResult] = useState<any>(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/merchants/card-wallets/balances`, { headers }),
      axios.get(`${API_URL}/merchants/withdrawal-config`, { headers }),
    ]).then(([balRes, cfgRes]) => {
      // API returns { wallets: [...], summary: {...} }
      const wallets = balRes.data?.wallets || [];
      const live = wallets
        .filter((w: any) => w.mode === 'production')
        .map((w: any) => ({
          currency: w.currency,
          balance: parseFloat(w.balance),
          mode: 'production',
        }));
      setBalances(live);
      if (live.length > 0) setCurrency(live[0].currency);
      setConfig(cfgRes.data);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const selectedBalance = balances.find(b => b.currency === currency);
  const phoneForMethod = config ? config[`phone_${method}`] : null;
  const isConfigured = config?.configured && config?.email;

  const requestOtp = async () => {
    if (!currency) {
      setError('No card wallet available. Contact support to activate card payments.');
      return;
    }
    if (!method || !amount || parseFloat(amount) <= 0) {
      setError('Select an operator and enter an amount');
      return;
    }
    if (!isConfigured) {
      setError('Please configure your cashout settings first (email + phone numbers).');
      return;
    }
    if (!phoneForMethod) {
      setError(`No phone number configured for ${method}. Go to Cashout Settings.`);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${UNIFIED_API}/merchant-withdrawal/request-otp`, {
        amount: parseFloat(amount),
        currency,
        method,
      }, { headers });

      if (res.data.success) {
        setReference(res.data.reference);
        setOtpMessage(res.data.message);
        setStep('otp');
      } else {
        setError(res.data.detail || 'Failed to send OTP');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to request OTP');
    }
    setSubmitting(false);
  };

  const confirmWithdrawal = async () => {
    if (otpCode.length !== 6) {
      setError('Enter the 6-digit OTP code');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${UNIFIED_API}/merchant-withdrawal/confirm`, {
        reference,
        otp_code: otpCode,
      }, { headers });

      setResult(res.data);
      setStep('result');

      // Refresh balances after withdrawal
      try {
        const balRes = await axios.get(`${API_URL}/merchants/card-wallets/balances`, { headers });
        const wallets = balRes.data?.wallets || [];
        const live = wallets
          .filter((w: any) => w.mode === 'production')
          .map((w: any) => ({ currency: w.currency, balance: parseFloat(w.balance), mode: 'production' }));
        setBalances(live);
      } catch {}
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Withdrawal failed');
    }
    setSubmitting(false);
  };

  // Poll status when processing
  useEffect(() => {
    if (step !== 'result' || !result?.reference) return;
    const st = (result?.status || '').toLowerCase();
    if (!['submitted', 'pending', 'queued', 'processing'].includes(st)) return;

    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${UNIFIED_API}/merchant-withdrawal/status/${result.reference}`, { headers });
        const newStatus = (res.data?.status || '').toLowerCase();
        if (newStatus === 'successful' || newStatus === 'completed') {
          setResult((prev: any) => ({ ...prev, status: 'Successful', detail: 'Withdrawal completed successfully' }));
          // Refresh balances
          try {
            const balRes = await axios.get(`${API_URL}/merchants/card-wallets/balances`, { headers });
            const wallets = balRes.data?.wallets || [];
            const live = wallets.filter((w: any) => w.mode === 'production').map((w: any) => ({ currency: w.currency, balance: parseFloat(w.balance), mode: 'production' }));
            setBalances(live);
          } catch {}
          clearInterval(interval);
        } else if (newStatus === 'failed' || newStatus === 'cancelled') {
          setResult((prev: any) => ({ ...prev, status: 'Failed', detail: res.data?.detail || 'Withdrawal failed' }));
          clearInterval(interval);
        }
      } catch {}
    }, 5000);

    return () => clearInterval(interval);
  }, [step, result?.reference, result?.status]);

  const reset = () => {
    setStep('form');
    setMethod('');
    setAmount('');
    setOtpCode('');
    setReference('');
    setResult(null);
    setError('');
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 text-white">
        <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="absolute -top-32 -right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-[100px]" />
        <div className="relative flex items-center gap-3">
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl">
            <ArrowDownToLine className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight">Card Withdrawal</h1>
            <p className="text-slate-400 text-xs">Withdraw from your card wallet to mobile money</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-rose-50 text-rose-700 text-sm flex items-center gap-2 border border-rose-200">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2">
          {step === 'form' && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm divide-y divide-gray-100">
              {/* Source: card wallet picker */}
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">Source — Card wallet</h3>
                  <span className="text-xs text-gray-400">{balances.length} {balances.length === 1 ? 'currency' : 'currencies'}</span>
                </div>
                {balances.length === 0 ? (
                  <p className="text-sm text-gray-500">No card wallet available. Contact support to activate card payments.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {balances.map(b => {
                      const selected = currency === b.currency;
                      return (
                        <button key={b.currency} type="button" onClick={() => setCurrency(b.currency)}
                          className={`px-4 py-3 rounded-xl border text-left transition-all ${selected ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-200' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                          <div className="flex items-baseline justify-between gap-2">
                            <span className={`text-xs font-semibold tracking-wide ${selected ? 'text-emerald-700' : 'text-gray-500'}`}>{b.currency}</span>
                            {selected && <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />}
                          </div>
                          <p className="mt-0.5 text-lg font-bold text-gray-900 tabular-nums">{b.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Operator picker */}
              <div className="p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Destination operator</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {OPERATORS.map(op => {
                    const selected = method === op.value;
                    const phone = config?.[`phone_${op.value}`];
                    return (
                      <button key={op.value} type="button" onClick={() => setMethod(op.value)}
                        className={`relative p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1.5 ${selected ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                        {selected && (
                          <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
                            <CheckCircle className="w-3 h-3 text-white" />
                          </span>
                        )}
                        <div className="w-12 h-12 rounded-lg bg-white border border-gray-100 flex items-center justify-center overflow-hidden">
                          <img src={op.logo} alt={op.label} className="w-9 h-9 object-contain" />
                        </div>
                        <span className="text-xs font-semibold text-gray-800">{op.label}</span>
                        {phone ? (
                          <span className={`text-[10px] font-mono ${selected ? 'text-emerald-700' : 'text-gray-400'}`}>{phone}</span>
                        ) : (
                          <span className="text-[10px] text-amber-600">Not configured</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Amount */}
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-900">Amount</label>
                  <button
                    type="button"
                    onClick={() => selectedBalance && setAmount(selectedBalance.balance.toFixed(2))}
                    disabled={!selectedBalance}
                    className="text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded disabled:opacity-50"
                  >
                    Max
                  </button>
                </div>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400">
                    {currency === 'CDF' ? 'FC' : currency === 'USD' ? '$' : currency}
                  </span>
                  <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                    placeholder="0.00" min="0" step="0.01"
                    max={selectedBalance?.balance}
                    className="w-full pl-12 pr-3 py-3 border border-gray-300 rounded-xl text-lg font-bold tabular-nums focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none" />
                </div>
                {selectedBalance && (
                  <p className="text-xs text-gray-500 mt-1.5">
                    Available <span className="tabular-nums font-medium text-gray-700">{selectedBalance.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })} {currency}</span>
                  </p>
                )}
                {amount && selectedBalance && parseFloat(amount) > selectedBalance.balance && (
                  <p className="text-xs text-rose-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Amount exceeds available balance
                  </p>
                )}
              </div>

              {/* Submit */}
              <div className="p-5 bg-gray-50/40">
                <button onClick={requestOtp}
                  disabled={submitting || !method || !amount || !currency || (selectedBalance != null && parseFloat(amount || '0') > selectedBalance.balance)}
                  className="w-full py-3 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2
                    enabled:bg-gradient-to-r enabled:from-emerald-600 enabled:to-emerald-700 enabled:text-white enabled:hover:from-emerald-700 enabled:hover:to-emerald-800 enabled:shadow-md
                    disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed">
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Sending OTP...</>
                  ) : (
                    <><Send className="w-4 h-4" /> Request OTP</>
                  )}
                </button>
              </div>
            </div>
          )}

          {step === 'otp' && (
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 space-y-5">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">Enter OTP Code</h3>
                <p className="text-xs text-gray-500 mt-1">{otpMessage}</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-gray-500">Amount</span><span className="font-bold">{parseFloat(amount).toLocaleString()} {currency}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Operator</span><span className="font-medium">{method}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Phone</span><span className="font-medium">{phoneForMethod}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Reference</span><span className="font-mono text-xs">{reference}</span></div>
              </div>

              <input type="text" value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000" maxLength={6}
                className="w-full border border-gray-300 rounded-xl px-4 py-4 text-2xl font-bold text-center tracking-[0.5em] focus:ring-2 focus:ring-blue-500" />

              <div className="flex gap-3">
                <button onClick={reset} className="flex-1 py-3 rounded-xl border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={confirmWithdrawal} disabled={submitting || otpCode.length !== 6}
                  className="flex-1 bg-emerald-600 text-white py-3 rounded-xl text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : 'Confirm Withdrawal'}
                </button>
              </div>
            </div>
          )}

          {step === 'result' && (() => {
            const st = (result?.status || '').toLowerCase();
            const isPending = st === 'pending_approval';
            const isProcessing = ['submitted', 'pending', 'queued', 'processing'].includes(st);
            const isSuccess = st === 'successful' || st === 'completed';
            const isFailed = st === 'failed' || st === 'cancelled';

            const icon = isPending ? (
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto bg-amber-100">
                <Shield className="w-7 h-7 text-amber-600" />
              </div>
            ) : isProcessing ? (
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto bg-blue-100">
                <Loader2 className="w-7 h-7 text-blue-600 animate-spin" />
              </div>
            ) : isSuccess ? (
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto bg-emerald-100">
                <CheckCircle className="w-7 h-7 text-emerald-600" />
              </div>
            ) : (
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto bg-red-100">
                <AlertCircle className="w-7 h-7 text-red-600" />
              </div>
            );

            const title = isPending ? 'Submitted for Approval'
              : isProcessing ? 'Processing Withdrawal'
              : isSuccess ? 'Withdrawal Successful'
              : 'Withdrawal Failed';

            const description = isPending
              ? 'Your withdrawal exceeds the direct limit and has been submitted for approval by the finance team. You will be notified once processed.'
              : isProcessing
              ? 'Your withdrawal is being processed by the mobile money operator. This usually takes a few seconds.'
              : result?.detail || result?.status;

            const badgeColor = isPending ? 'bg-amber-100 text-amber-700'
              : isProcessing ? 'bg-blue-100 text-blue-700'
              : isSuccess ? 'bg-emerald-100 text-emerald-700'
              : 'bg-red-100 text-red-700';

            const badgeLabel = isPending ? 'Pending Approval'
              : isProcessing ? 'Processing'
              : result?.status;

            return (
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 text-center space-y-4">
                {icon}
                <h3 className="text-base font-semibold text-gray-900">{title}</h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto">{description}</p>

                <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-1 text-left">
                  <div className="flex justify-between"><span className="text-gray-500">Reference</span><span className="font-mono text-xs">{result?.reference}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Status</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}>{badgeLabel}</span>
                  </div>
                </div>

                {isProcessing && (
                  <p className="text-xs text-blue-600 flex items-center justify-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Waiting for confirmation from operator...
                  </p>
                )}

                <button onClick={reset} className="w-full py-3 rounded-xl border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50">
                  New Withdrawal
                </button>
              </div>
            );
          })()}
        </div>

        {/* Right: Info */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-5 shadow-lg border border-gray-200">
            <h3 className="text-xs font-semibold text-gray-900 mb-3">How it works</h3>
            <ol className="space-y-2.5 text-xs text-gray-600">
              <li className="flex gap-2"><span className="w-5 h-5 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0">1</span>Select operator and enter amount</li>
              <li className="flex gap-2"><span className="w-5 h-5 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0">2</span>Receive OTP code on your email</li>
              <li className="flex gap-2"><span className="w-5 h-5 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0">3</span>Enter OTP to confirm withdrawal</li>
              <li className="flex gap-2"><span className="w-5 h-5 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0">4</span>Receive funds on your mobile money</li>
            </ol>
          </div>

          {!isConfigured && (
            <Link
              to="/cashout-settings"
              className="block bg-amber-50 border border-amber-200 rounded-2xl p-4 hover:bg-amber-100 transition-colors group"
            >
              <div className="flex items-start gap-2">
                <Settings className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-amber-800 font-semibold">Cashout settings required</p>
                  <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
                    Configure your email and phone numbers before making a withdrawal.
                  </p>
                  <span className="inline-flex items-center gap-1 mt-2 text-[11px] font-medium text-amber-800 group-hover:underline">
                    Open Cashout Settings <ChevronRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </Link>
          )}
          {isConfigured && (
            <Link
              to="/cashout-settings"
              className="flex items-center justify-between text-xs text-gray-500 hover:text-gray-700 px-1"
            >
              <span className="inline-flex items-center gap-1.5">
                <Settings className="w-3.5 h-3.5" /> Manage cashout numbers
              </span>
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
